import {
  ApiErrorSchema,
  ENDPOINTS,
  UploadBundleExecuteRequestSchema,
  UploadBundlePlanRequestSchema,
  UploadOptionsRequestSchema,
  UploadRequestSchema,
  type UploadBundleExecuteRequest,
  type UploadBundleExecuteResponse,
  type UploadBundlePlanRequest,
  type UploadBundlePlanResponse,
  type SelectableParty,
  type PartyType,
  type UploadOptionsRequest,
  type UploadOptionsResponse,
  type UploadRequest,
  type UploadResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';

/**
 * `upload-options` and `upload` (CLM-30) — the guided manual intake pair.
 *
 * Same three properties as `ingest.ts`, and for the same reasons:
 *
 *  - **the path comes from the endpoint registry**, never a string literal, so
 *    the SPA cannot drift from the contract of record;
 *  - **the request is validated through the shared schema before it leaves the
 *    browser.** Both schemas are `.strict()`, so an invented or scope-shaped
 *    field is a thrown `ZodError` here rather than a payload on the wire. For
 *    `upload` that is FR-14 doing real work in the client too: there is no
 *    `contract_key` field to send, so a form that tried would fail to compile
 *    and a hand-built object would fail to parse;
 *  - **the response is parsed, not asserted.** A response that does not parse is
 *    a defect to report, never data to render.
 *
 * Rule 11 holds: this posts a file and a JSON blob to the gateway. The browser
 * never speaks to Graph, never learns a drive id, and never learns the path CLM
 * files the document under — the server derives that from the party, the type and
 * the digest.
 *
 * CLM-118 (D-110-3) — neither request carries a type. The options read carries
 * the chosen file's own name as `client_file_name`, so the server answers with
 * what its deterministic file-name rule reads from it (`name_type`); the name is
 * evidence for the type, never a path and never stored.
 */
const OPTIONS_ENDPOINT = ENDPOINTS['upload-options'];
const UPLOAD_ENDPOINT = ENDPOINTS.upload;

/** The part names the gateway accepts. Server-named; both are required. */
export const METADATA_PART = 'metadata';
export const DOCUMENT_PART = 'document';

export async function fetchUploadOptions(
  request: UploadOptionsRequest,
  signal?: AbortSignal,
): Promise<UploadOptionsResponse> {
  const { page, ...flat } = UploadOptionsRequestSchema.parse(request);
  const query: Record<string, string | number | undefined> = { ...flat };
  /* `page` is flattened to dotted keys — `apiGet` does not expand a nested object. */
  if (page?.limit !== undefined) query['page.limit'] = page.limit;
  if (page?.cursor !== undefined) query['page.cursor'] = page.cursor;
  const raw = await apiGet<unknown>(OPTIONS_ENDPOINT.path, {
    query: query satisfies Query,
    ...(signal === undefined ? {} : { signal }),
  });
  return OPTIONS_ENDPOINT.response.parse(raw);
}

/** One page of the scoped selectable-party list, as the picker consumes it. */
export interface SelectablePartyPage {
  readonly parties: readonly SelectableParty[];
  readonly parties_page: UploadOptionsResponse['parties_page'];
}

/** The picker's page size — the server's own default, made explicit on the wire. */
export const PARTY_SEARCH_PAGE_LIMIT = 50;

/**
 * Search the parties this caller may file against — `PartyPicker`'s one request
 * (CLM-68). The same `upload-options` read as {@link fetchUploadOptions}, with
 * `party_search` and `page`, so the list is the **scoped** one (`selectable-
 * parties` carries the scope predicate; `resolve-party` does not — D-68-1).
 *
 * `page` travels as the dotted keys `page.limit` / `page.cursor` because
 * `apiGet` stringifies values and does not expand a nested object, and the API
 * decodes dotted paths — the same flattening `resolvePartyQuery` does.
 *
 * CLM-78 (B3) — `partyType` narrows the list to one `party_type` (`'Vendor'` is
 * what `VendorPicker` sends); the scope predicate still applies underneath
 * (rule 9). Omitted, the request is byte-identical to before.
 */
export async function searchSelectableParties(
  search: string,
  cursor: string | undefined,
  signal?: AbortSignal,
  partyType?: PartyType,
): Promise<SelectablePartyPage> {
  const validated = UploadOptionsRequestSchema.parse({
    party_search: search,
    page: cursor === undefined ? { limit: PARTY_SEARCH_PAGE_LIMIT } : { limit: PARTY_SEARCH_PAGE_LIMIT, cursor },
    ...(partyType === undefined ? {} : { party_type: partyType }),
  });
  const query: Record<string, string | number> = {
    party_search: validated.party_search ?? search,
    'page.limit': validated.page?.limit ?? PARTY_SEARCH_PAGE_LIMIT,
  };
  if (validated.page?.cursor !== undefined) query['page.cursor'] = validated.page.cursor;
  if (validated.party_type !== undefined) query['party_type'] = validated.party_type;
  const raw = await apiGet<unknown>(OPTIONS_ENDPOINT.path, {
    query: query satisfies Query,
    ...(signal === undefined ? {} : { signal }),
  });
  const response = OPTIONS_ENDPOINT.response.parse(raw);
  return { parties: response.parties, parties_page: response.parties_page };
}

/**
 * Post the guided form.
 *
 * The body is a `FormData` with exactly two parts. `metadata` is a `Blob` with
 * `type: 'application/json'` rather than a plain string, because a string part
 * gets `content-type: text/plain` and the server refuses to guess: the part's
 * declared type is what tells it this is the JSON half.
 *
 * `client.ts` leaves `Content-Type` to the browser for a `FormData` body, which
 * is not a convenience — it is required. The header has to carry the multipart
 * boundary the browser generated, and any value set by hand would either omit it
 * or disagree with the body.
 *
 * The document is sent as the `File` the user chose, so its bytes are exactly
 * what the digest in `metadata` was computed over.
 */
export async function submitUpload(
  request: UploadRequest,
  document: File,
  signal?: AbortSignal,
): Promise<UploadResponse> {
  const metadata = UploadRequestSchema.parse(request);

  const form = new FormData();
  form.append(
    METADATA_PART,
    new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
  );
  /**
   * The third argument is the part's `filename`. It is the user's own name for
   * the file and the server **logs it and never uses it as a path** — the
   * SharePoint path is derived from `(party, type, digest)`. It is sent because a
   * file part with no filename is not what a browser produces, and an operator
   * looking at a log line wants to know what the user called the thing they
   * uploaded.
   */
  form.append(DOCUMENT_PART, document, document.name);

  const raw = await apiPost<unknown>(
    UPLOAD_ENDPOINT.path,
    form,
    signal === undefined ? {} : { signal },
  );
  return UPLOAD_ENDPOINT.response.parse(raw);
}

/**
 * SHA-256 of a file, as lower-case hex — computed in the browser, over the exact
 * bytes that are about to be posted.
 *
 * `UploadDocumentDescriptorSchema.sha256` is not decoration and it is not an
 * integrity claim against a hostile client: the server recomputes the digest and
 * refuses a mismatch, and the digest it uses is always its own. What the client's
 * value buys is a *truncation* check — a file that was cut short between being
 * read here and arriving there disagrees, and the upload is refused rather than
 * filed as a valid but incomplete contract.
 *
 * `crypto.subtle` is available in every browser this app supports and requires a
 * secure context, which Static Web Apps always is.
 */
export async function sha256OfFile(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The one field error a refusal carries, when it carries one.
 *
 * The gateway's bodies are uniform by design — one `code` and one `message` per
 * rejection kind, never per cause (`apps/api/src/rejections.ts` rule 1) — with
 * two permitted variations: `field_errors`, admitted for the kinds that describe
 * the caller's own payload or a row the caller may already read, and `reason` on
 * every `403`, a fixed code about the caller's own sign-in. For a
 * `409` that is the difference this form has to act on: *this document is already
 * filed as `SGW-SOW-0004`* (stop, open that contract) is not *the sequence number
 * moved* (re-open the form and submit again), and both arrive as `409 conflict`
 * with the identical message.
 *
 * Parsed, never asserted: an error body that does not match the contract yields
 * `null` and the caller falls back to the generic copy.
 */
export function refusalFieldError(body: unknown): { path: string; message: string } | null {
  const parsed = ApiErrorSchema.safeParse(body);
  const first = parsed.success ? parsed.data.field_errors[0] : undefined;
  return first === undefined ? null : { path: first.path, message: first.message };
}

/* -------------------------------------------------------------------------- */
/* CLM-57 — several documents: plan, then execute                              */
/* -------------------------------------------------------------------------- */

const BUNDLE_PLAN_ENDPOINT = ENDPOINTS['upload-bundle-plan'];
const BUNDLE_EXECUTE_ENDPOINT = ENDPOINTS['upload-bundle-execute'];

/**
 * The two bundle calls build their `FormData` exactly as `submitUpload` does —
 * `metadata` as an `application/json` Blob, `document` as the user's own `File`
 * — because the gateway decodes the same two server-named parts for all three
 * endpoints. The bundle is the same `File` on plan and on execute: the plan is
 * not persisted (rule 8), so the server re-expands what is re-sent and refuses
 * with `409` if the digest moved.
 *
 * Both metadata schemas are `.strict()` and carry no `contract_key` and no
 * `sequence_number` — FR-14 in the client as in the contract.
 */
function bundleForm(metadata: unknown, bundle: File): FormData {
  const form = new FormData();
  form.append(METADATA_PART, new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append(DOCUMENT_PART, bundle, bundle.name);
  return form;
}

export async function planUploadBundle(
  request: UploadBundlePlanRequest,
  bundle: File,
  signal?: AbortSignal,
): Promise<UploadBundlePlanResponse> {
  const metadata = UploadBundlePlanRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    BUNDLE_PLAN_ENDPOINT.path,
    bundleForm(metadata, bundle),
    signal === undefined ? {} : { signal },
  );
  return BUNDLE_PLAN_ENDPOINT.response.parse(raw);
}

export async function executeUploadBundle(
  request: UploadBundleExecuteRequest,
  bundle: File,
  signal?: AbortSignal,
): Promise<UploadBundleExecuteResponse> {
  const metadata = UploadBundleExecuteRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    BUNDLE_EXECUTE_ENDPOINT.path,
    bundleForm(metadata, bundle),
    signal === undefined ? {} : { signal },
  );
  return BUNDLE_EXECUTE_ENDPOINT.response.parse(raw);
}
